import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ButtonComponent, ButtonVariant } from '../button/button.component';
import { ModalComponent } from '../modal/modal.component';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'primary' | 'success';

type AccentClasses = { box: string; iconWrap: string; title: string; sub: string };

/**
 * Boîte de dialogue de confirmation unique du design system.
 *
 * Couvre toutes les actions nécessitant une confirmation (suppression,
 * modification, validation, activation…). La confirmation est un simple clic, sans
 * ressaisie de l'adresse email.
 *
 * Le message est soit fourni via `message`, soit composé depuis
 * `itemType` + `itemLabel` (« Vous êtes sur le point de supprimer … »).
 */
@Component({
    selector: 'app-confirm-dialog',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ModalComponent, ButtonComponent, MatIconModule],
    templateUrl: './confirm-dialog.component.html',
})
export class ConfirmDialogComponent {
    readonly open = input.required<boolean>();
    readonly title = input<string>('Confirmer la suppression');
    /** Message principal. Si vide, composé depuis `itemType` + `itemLabel`. */
    readonly message = input<string>('');
    readonly itemType = input<string>('l’élément');
    readonly itemLabel = input<string>('');
    readonly confirmLabel = input<string>('Confirmer la suppression');
    readonly cancelLabel = input<string>('Annuler');
    readonly variant = input<ConfirmDialogVariant>('danger');
    readonly confirmIcon = input<string>('delete');
    readonly loading = input<boolean>(false);
    /** Avertissement secondaire affiché sous le message. */
    readonly warning = input<string>('Cette action est irréversible.');
    readonly cancelled = output<void>();
    readonly confirmed = output<void>();

    readonly bodyText = computed(() => {
        const msg = this.message().trim();
        if (msg) {
            return msg;
        }
        const label = this.itemLabel().trim();

        return `Vous êtes sur le point de supprimer ${this.itemType()}${label ? ' « ' + label + ' »' : ''}.`;
    });

    readonly buttonVariant = computed<ButtonVariant>(() => {
        switch (this.variant()) {
            case 'warning':
                return 'warning';
            case 'success':
                return 'success';
            case 'primary':
                return 'primary';
            default:
                return 'danger';
        }
    });

    readonly icon = computed(() => {
        switch (this.variant()) {
            case 'warning':
                return 'warning_amber';
            case 'success':
                return 'check_circle';
            case 'primary':
                return 'help';
            default:
                return 'warning';
        }
    });

    readonly accent = computed<AccentClasses>(() => {
        switch (this.variant()) {
            case 'warning':
                return {
                    box: 'bg-amber-50 border-amber-100',
                    iconWrap: 'bg-amber-100 text-amber-600',
                    title: 'text-amber-900',
                    sub: 'text-amber-700',
                };
            case 'success':
                return {
                    box: 'bg-dgpe-primary-50 border-dgpe-primary-100',
                    iconWrap: 'bg-dgpe-primary-100 text-dgpe-primary-600',
                    title: 'text-dgpe-primary-900',
                    sub: 'text-dgpe-primary-700',
                };
            case 'primary':
                return {
                    box: 'bg-dgpe-primary-500/5 border-dgpe-primary-500/15',
                    iconWrap: 'bg-dgpe-primary-500/10 text-dgpe-primary-500',
                    title: 'text-gray-900',
                    sub: 'text-gray-600',
                };
            default:
                return { box: 'bg-red-50 border-red-100', iconWrap: 'bg-red-100 text-red-600', title: 'text-red-900', sub: 'text-red-700' };
        }
    });

    onCancel(): void {
        this.cancelled.emit();
    }

    onConfirm(): void {
        if (!this.loading()) {
            this.confirmed.emit();
        }
    }
}
